import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import getServices from "../../Services/get-service";
import postService from "../../Services/post-service";
import putService from "../../Services/put-service";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import InvestmentCard from "./InvestmentCard";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import {
  Divider,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const InvestmentPlan = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [categoryName] = useState(location.state?.name || "");
  const [actionLoading, setActionLoading] = useState(false);

  const [openDateModal, setOpenDateModal] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState("");
  const [openRoiModal, setOpenRoiModal] = useState(false);
  const [newRoi, setNewRoi] = useState("");

  // Maturity modal state
  const [openMaturityModal, setOpenMaturityModal] = useState(false);
  const [finalRealizedValue, setFinalRealizedValue] = useState("");
  const [durationMonths, setDurationMonths] = useState("");
  const [maturityConcludingRate, setMaturityConcludingRate] = useState("");
  const [maturityNotes, setMaturityNotes] = useState("");
  const [maturityResult, setMaturityResult] = useState(null);
  const [openMaturityResultModal, setOpenMaturityResultModal] = useState(false);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await getServices.getInvestmentPlan(categoryId);
      if (res?.data?.status === "2000" && res.data?.data?.investments) {
        setPlans(res.data.data.investments);
      } else {
        setPlans([]);
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [categoryId]);

  // Parse acceptedTenureMonths — handles both array and JSON string like "[3,6,9]"
  const parseTenureMonths = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const handleUpdateRoi = async () => {
    if (!selectedPlan || !newRoi) return;
    try {
      setActionLoading(true);
      const res = await putService.updateRoi(selectedPlan.investmentId, {
        concludingRate: parseFloat(newRoi),
      });
      if (res?.data?.status === "2000") {
        toast.success("ROI updated successfully");
        await fetchPlans();
        setSelectedPlan((prev) => ({
          ...prev,
          concludingRate: parseFloat(newRoi),
        }));
        setOpenRoiModal(false);
        setNewRoi("");
      } else {
        toast.error(res?.data?.description || "Action failed");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.description || "An error occurred updating ROI",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async (actionType) => {
    if (!selectedPlan) return;
    if (actionType === "open" && !subscriptionEndDate) {
      setOpenDateModal(true);
      return;
    }
    try {
      setActionLoading(true);
      let res;
      if (actionType === "open") {
        res = await putService.openSubscription(selectedPlan.investmentId, {
          subscriptionEndDate,
        });
      } else if (actionType === "close") {
        res = await putService.closeSubscription(selectedPlan.investmentId);
      } else if (actionType === "activate") {
        res = await putService.activateInvestmentPlan(
          selectedPlan.investmentId,
        );
      }
      if (res?.data?.status === "2000") {
        toast.success(`Investment ${actionType}ed successfully`);
        fetchPlans();
        setOpenDateModal(false);
        setSubscriptionEndDate("");
        setSelectedPlan(null);
      } else {
        toast.error(res?.data?.description || "Action failed");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.description ||
          "An error occurred. Please try again.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleExecuteMaturity = async () => {
    if (
      !selectedPlan ||
      !finalRealizedValue ||
      !durationMonths ||
      !maturityConcludingRate
    )
      return;
    try {
      setActionLoading(true);
      const res = await postService.matureInvestment(
        selectedPlan.investmentId,
        {
          finalRealizedValue: parseFloat(finalRealizedValue),
          durationMonths: parseInt(durationMonths),
          concludingRate: parseFloat(maturityConcludingRate),
          notes: maturityNotes,
        },
      );
      if (res?.data?.status === "2000") {
        toast.success("Investment matured successfully");
        setMaturityResult(res.data.data);
        setOpenMaturityModal(false);
        setOpenMaturityResultModal(true);
        setFinalRealizedValue("");
        setDurationMonths("");
        setMaturityConcludingRate("");
        setMaturityNotes("");
        fetchPlans();
      } else {
        toast.error(res?.data?.description || "Maturity execution failed");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.description ||
          "An error occurred executing maturity",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedPlan(null);
    setSubscriptionEndDate("");
    setFinalRealizedValue("");
    setDurationMonths("");
    setMaturityConcludingRate("");
    setMaturityNotes("");
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-back-color2 capitalize">
          {categoryName || "Investment"} Plans
        </h2>
        <button
          onClick={() => navigate("/investment")}
          className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to Categories
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 bg-white border rounded-lg shadow-sm">
              <Skeleton height={30} width="80%" />
              <Skeleton count={3} />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <InvestmentCard
              key={plan.investmentId}
              title={plan.name}
              description={
                <div className="flex flex-col gap-4">
                  <p className="text-gray-600 line-clamp-2">
                    {plan.description}
                  </p>
                  <div className="text-xs font-bold uppercase tracking-tight">
                    {plan.concludingRate
                      ? `ROI: ${plan.concludingRate}% (Final)`
                      : `ROI: ${plan.minRate}% - ${plan.maxRate}%`}
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="text-gray-500">Status: {plan.status}</span>
                  </div>
                </div>
              }
              onClick={() => setSelectedPlan(plan)}
            />
          ))}
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedPlan)}
        onClose={handleCloseDrawer}
        sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", md: 500 } } }}
      >
        {selectedPlan && (
          <div className="px-4 h-full flex flex-col">
            <div className="flex my-4 justify-between items-center px-2">
              <h1 className="text-xl md:text-2xl font-bold text-back-color2">
                Investment Details
              </h1>
              <div
                className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
                onClick={handleCloseDrawer}
              >
                <CloseIcon />
              </div>
            </div>
            <Divider />

            <div className="px-2 md:px-5 pt-3 flex-1 overflow-y-auto">
              <div className="mt-5 space-y-6">
                {selectedPlan.imageUrl && (
                  <img
                    src={selectedPlan.imageUrl}
                    alt={selectedPlan.name}
                    className="w-full h-48 object-cover rounded-lg shadow-sm"
                  />
                )}

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-gray-400 text-xs font-bold uppercase">
                      Plan Name
                    </label>
                    <p className="text-lg font-semibold text-gray-800">
                      {selectedPlan.name}
                    </p>
                  </div>
                  <Divider />

                  <div
                    onClick={() => navigate(`${selectedPlan.investmentId}`)}
                    className="group flex items-center justify-between p-5 bg-violet-600 rounded-2xl cursor-pointer hover:bg-violet-700 transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 p-2 rounded-lg">
                        <TrendingUpIcon className="text-white" />
                      </div>
                      <p className="text-white font-bold">
                        View Investment Subscribers
                      </p>
                    </div>
                    <ArrowForwardIosIcon className="text-white text-xs group-hover:translate-x-1" />
                  </div>

                  <Divider />

                  <div>
                    <label className="text-gray-400 text-xs font-bold uppercase tracking-wider">
                      Description
                    </label>
                    <p className="text-gray-600 mt-1 leading-relaxed text-sm md:text-base">
                      {selectedPlan.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl">
                    {/* Tenure months as tags */}
                    <div>
                      <label className="text-gray-400 text-[10px] font-bold uppercase">
                        Accepted Tenure
                      </label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {parseTenureMonths(selectedPlan.acceptedTenureMonths)
                          .length > 0 ? (
                          parseTenureMonths(
                            selectedPlan.acceptedTenureMonths,
                          ).map((month) => (
                            <span
                              key={month}
                              className="inline-flex items-center bg-violet-100 text-violet-700 text-xs font-semibold px-2.5 py-1 rounded-full"
                            >
                              {month} {Number(month) === 1 ? "month" : "months"}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="text-gray-400 text-[10px] font-bold uppercase">
                        Price Per Unit
                      </label>
                      <p className="font-bold text-gray-800">
                        ₦{selectedPlan.unitPrice?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                    <div>
                      <label className="text-gray-400 text-xs font-bold uppercase">
                        Current Status
                      </label>
                      <div className="mt-1">
                        <span className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-bold uppercase">
                          {selectedPlan.status}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="text-gray-400 text-xs font-bold uppercase">
                        ROI
                      </label>
                      <div className="mt-1 flex items-center justify-between bg-violet-50 p-2 rounded-lg border border-violet-100">
                        <span className="text-violet-700 text-xs font-bold uppercase">
                          {selectedPlan.concludingRate
                            ? `${selectedPlan.concludingRate}% (Final)`
                            : `${selectedPlan.minRate}% - ${selectedPlan.maxRate}%`}
                        </span>
                        <button
                          onClick={() => {
                            setNewRoi(selectedPlan.maxRate);
                            setOpenRoiModal(true);
                          }}
                          className="text-[10px] bg-violet-600 text-white px-2 py-1 rounded hover:bg-violet-700 font-bold"
                        >
                          UPDATE
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 md:p-6 border-t bg-white">
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleAction("open")}
                  className="w-full py-3.5 border-2 border-violet-500 text-violet-500 rounded-xl font-bold hover:bg-violet-50"
                >
                  {actionLoading ? "Processing..." : "Open Subscription"}
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleAction("close")}
                    className="flex-1 py-3.5 border-2 border-violet-500 text-violet-500 rounded-xl font-bold hover:bg-violet-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleAction("activate")}
                    className="flex-1 py-3.5 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700"
                  >
                    Activate Plan
                  </button>
                </div>
                {/* Execute Maturity */}
                <button
                  onClick={() => setOpenMaturityModal(true)}
                  className="w-full py-3.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors"
                >
                  Execute Maturity
                </button>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Update ROI Modal */}
      <Dialog
        open={openRoiModal}
        onClose={() => setOpenRoiModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className="text-back-color2 font-bold">
          Update Concluding ROI
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-500 mb-4">
            Set the final return rate for subscribers of this plan.
          </p>
          <div className="relative">
            <input
              type="number"
              className="w-full border p-3 rounded-lg outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="12.5"
              value={newRoi}
              onChange={(e) => setNewRoi(e.target.value)}
            />
            <span className="absolute right-4 top-3 text-gray-400">%</span>
          </div>
        </DialogContent>
        <DialogActions className="p-4">
          <button
            onClick={() => setOpenRoiModal(false)}
            className="px-4 py-2 text-gray-600"
          >
            Cancel
          </button>
          <button
            disabled={!newRoi || actionLoading}
            onClick={handleUpdateRoi}
            className={`px-6 py-2 rounded-lg font-bold text-white ${
              !newRoi ? "bg-gray-300" : "bg-violet-500 hover:bg-violet-700"
            }`}
          >
            {actionLoading ? "Updating..." : "Update ROI"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Subscription End Date Modal */}
      <Dialog
        open={openDateModal}
        onClose={() => setOpenDateModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className="text-back-color2 font-bold">
          Set Subscription End Date
        </DialogTitle>
        <DialogContent>
          <input
            type="date"
            className="w-full border p-3 rounded-lg mt-2"
            value={subscriptionEndDate}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => setSubscriptionEndDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="p-4">
          <button onClick={() => setOpenDateModal(false)} className="px-4 py-2">
            Cancel
          </button>
          <button
            disabled={!subscriptionEndDate || actionLoading}
            onClick={() => handleAction("open")}
            className="px-6 py-2 rounded-lg font-bold text-white bg-violet-500 hover:bg-violet-700"
          >
            Confirm & Open
          </button>
        </DialogActions>
      </Dialog>

      {/* Execute Maturity Modal */}
      <Dialog
        open={openMaturityModal}
        onClose={() => {
          setOpenMaturityModal(false);
          setFinalRealizedValue("");
          setDurationMonths("");
          setMaturityConcludingRate("");
          setMaturityNotes("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className="font-bold text-gray-800">
          Execute Maturity
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-500 mb-4">
            This will mature the investment plan and distribute payouts to all
            subscribers.
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                Final Realized Value (₦)
              </label>
              <input
                type="number"
                className="w-full border p-3 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="50000"
                value={finalRealizedValue}
                onChange={(e) => setFinalRealizedValue(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                  Duration (Months)
                </label>
                <select
                  className="w-full border p-3 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(e.target.value)}
                >
                  <option value="">Select</option>
                  {parseTenureMonths(selectedPlan?.acceptedTenureMonths)
                    .length > 0
                    ? parseTenureMonths(selectedPlan.acceptedTenureMonths).map(
                        (m) => (
                          <option key={m} value={m}>
                            {m} {Number(m) === 1 ? "month" : "months"}
                          </option>
                        ),
                      )
                    : Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <option key={m} value={m}>
                          {m} {m === 1 ? "month" : "months"}
                        </option>
                      ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                  Concluding Rate (%)
                </label>
                <input
                  type="number"
                  className="w-full border p-3 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder=""
                  value={maturityConcludingRate}
                  onChange={(e) => setMaturityConcludingRate(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                Notes (optional)
              </label>
              <textarea
                className="w-full border p-3 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 h-24 resize-none text-sm"
                placeholder="Q1 maturity run"
                value={maturityNotes}
                onChange={(e) => setMaturityNotes(e.target.value)}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <button
            onClick={() => {
              setOpenMaturityModal(false);
              setFinalRealizedValue("");
              setDurationMonths("");
              setMaturityConcludingRate("");
              setMaturityNotes("");
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            disabled={
              !finalRealizedValue ||
              !durationMonths ||
              !maturityConcludingRate ||
              actionLoading
            }
            onClick={handleExecuteMaturity}
            className={`px-6 py-2 rounded-lg font-bold text-white transition-colors ${
              !finalRealizedValue || !durationMonths || !maturityConcludingRate
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {actionLoading ? "Processing..." : "Confirm & Execute"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Maturity Result Modal */}
      <Dialog
        open={openMaturityResultModal}
        onClose={() => setOpenMaturityResultModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="font-bold text-gray-800 flex items-center gap-2">
          <span className="text-emerald-600">✓</span> Maturity Executed
          Successfully
        </DialogTitle>
        <DialogContent>
          {maturityResult && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Concluding Rate",
                    value: `${maturityResult.concludingRate}%`,
                  },
                  {
                    label: "Profit Per Unit",
                    value: `₦${maturityResult.profitPerUnit?.toLocaleString()}`,
                  },
                  {
                    label: "Subscriptions Matured",
                    value: maturityResult.subscriptionsMatured,
                  },
                  {
                    label: "Total Principal",
                    value: `₦${maturityResult.totalPrincipal?.toLocaleString()}`,
                  },
                  {
                    label: "Total Profit Distributed",
                    value: `₦${maturityResult.totalProfitDistributed?.toLocaleString()}`,
                  },
                  {
                    label: "Total Payout Amount",
                    value: `₦${maturityResult.totalPayoutAmount?.toLocaleString()}`,
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-[10px] font-bold uppercase text-gray-400">
                      {label}
                    </p>
                    <p className="text-sm font-bold text-gray-800 mt-0.5">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              {maturityResult.notes && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-[10px] font-bold uppercase text-gray-400">
                    Notes
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {maturityResult.notes}
                  </p>
                </div>
              )}
              {maturityResult.poolId && (
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-[10px] font-bold uppercase text-emerald-600">
                    Pool ID
                  </p>
                  <p className="text-sm font-mono text-emerald-700 mt-0.5">
                    {maturityResult.poolId}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions className="p-4">
          <button
            onClick={() => setOpenMaturityResultModal(false)}
            className="px-6 py-2 rounded-lg font-bold text-white bg-emerald-600 hover:bg-emerald-700"
          >
            Done
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InvestmentPlan;
